import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useFocusTrap } from "../hooks/useFocusTrap";
import { API_BASE, authHeaders } from '../constants/api';
import { severityLabels } from '../constants/alarmConstants';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { updateAlarmStatusAPI } from '../utils/alarmActions';
import { formatTelemetryTimestamp } from '../utils/formatDate';
import Pagination from './Pagination';
import { useAuth } from '../context/AuthContext';
import "./GroupDetails.css";
import { Zurück, Export, AiAnalyse2, Löschen, Hinzufügen, Hinweis, Abbrechen } from '../icons';
import { useStatusMessage } from '../hooks/useStatusMessage';
import { StatusBar, DeleteConfirmModal, IconButton, ModalDeviceSearch, AlarmStatusDropdown } from '../UI-Elements';

const PAGE_SIZE = 5;

function GroupDetail({ groupId: propGroupId }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const groupId = propGroupId || params.groupId;
  const { authToken: token } = useAuth();

  const [group, setGroup] = useState(() => {
    const nameFromState = location && location.state && location.state.name;
    return nameFromState ? { name: nameFromState } : null;
  });

  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);

  const titleRef = useRef(null);
  const addDevicesModalRef = useRef(null);
  const deviceSuggestionsRef = useRef(null);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);
  const [devices, setDevices] = useState([]);
  const [totalDevices, setTotalDevices] = useState(0);
  const [telemetry, setTelemetry] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [openAlarmStatusMenu, setOpenAlarmStatusMenu] = useState(null);

  // Add devices modal
  const [showAddDevicesModal, setShowAddDevicesModal] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceSuggestions, setDeviceSuggestions] = useState([]);
  const [showDeviceSuggestions, setShowDeviceSuggestions] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [addingDevices, setAddingDevices] = useState(false);
  const [showAddDevicesTooltip, setShowAddDevicesTooltip] = useState(false);
  const tooltipRef = useRef(null);

  const [showGroupDeleteConfirm, setShowGroupDeleteConfirm] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);

  const [entityToRemove, setEntityToRemove] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  const { statusMessage, statusType, showStatus } = useStatusMessage();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const closeAlarmStatusMenu = useCallback(() => setOpenAlarmStatusMenu(null), []);
  useDropdownClose(openAlarmStatusMenu, closeAlarmStatusMenu, '.alarm-status-wrapper');

  // Focus trap für Modals
  useFocusTrap(showAddDevicesModal, addDevicesModalRef);

  // Focus the title when loading completes so screenreaders announce context
  useEffect(() => {
    if (!loading && group && titleRef.current) {
      try {
        titleRef.current.focus();
      } catch {}
    }
  }, [loading, group]);

  useEffect(() => {
    if (!token) { setDeviceSuggestions([]); return; }

    const controller = new AbortController();

    async function searchDevices() {
      try {
        const params = new URLSearchParams({ pageSize: 10, page: 0 });
        if (deviceSearch) params.set('textSearch', deviceSearch);
        const res = await fetch(
          `${API_BASE}/tenant/devices?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          }
        );

        if (!res.ok) return;

        const data = await res.json();

        const existingIds = new Set(devices.map(d => d.id.id));
        const selectedIds = new Set(selectedDevices.map(d => d.id));

        setDeviceSuggestions(
          (data.data || []).filter(
            d => !existingIds.has(d.id.id) && !selectedIds.has(d.id.id)
          )
        );
      } catch {}
    }

    searchDevices();
    return () => controller.abort();
  }, [deviceSearch, devices, selectedDevices, token]);

  const loadAll = useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const headers = authHeaders(token);

        const base = API_BASE;

        const params = new URLSearchParams({
          pageSize: PAGE_SIZE,
          page,
          sortProperty: "createdTime",
          sortOrder: "DESC"
        });

        const devicesRes  = await fetch(
          `${base}/entityGroup/${groupId}/entities?${params.toString()}`,
          { headers }
        );
        const devicesData  = await devicesRes.json();

        const [
          groupRes,
          telemetryRes,
          dashboardsRes,
          alarmsRes
        ] = await Promise.all([
          fetch(`${base}/entityGroup/${groupId}`, { headers }),
          fetch(`${base}/plugins/telemetry/DEVICE/${groupId}/values/timeseries`, { headers }),
          fetch(`${base}/dashboardInfos?deviceId=${groupId}`, { headers }),
          fetch(`${base}/alarms?entityType=DEVICE&entityId=${groupId}&pageSize=10&page=0`, { headers })
        ]);

        if (!groupRes.ok) {
          const errData = await groupRes.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Gruppendaten konnten nicht geladen werden (HTTP ${groupRes.status})`);
        }
        const groupData = await groupRes.json();
        const telemetryData = await telemetryRes.json().catch(() => ({}));
        const dashboardsData = await dashboardsRes.json().catch(() => ({ data: [] }));
        const alarmsData = await alarmsRes.json().catch(() => ({ data: [] }));

        setGroup(groupData);
        setDevices(devicesData.data || []);
        setTotalDevices(devicesData.totalElements ?? 0);
        setTotalPages(Math.ceil((devicesData.totalElements ?? 0) / PAGE_SIZE));
        setTelemetry(telemetryData || []);
        setDashboards(dashboardsData.data || []);
        setAlarms((alarmsData.data || []).filter(a => a.originator.id === groupId));

      } catch (err) {
        console.error(err);
        setError(err.message || "Gruppendaten konnten nicht geladen werden. Bitte prüfen Sie Ihre Netzwerkverbindung.");
      } finally {
        setLoading(false);
      }
  }, [token, groupId, page]);

  useEffect(() => {
    if (groupId && token) {
      loadAll();
    }
  }, [groupId, token, page, loadAll]);

  function addDevice(device) {
    setSelectedDevices(prev => [
      ...prev,
      { id: device.id.id, name: device.name }
    ]);
    setDeviceSearch("");
    setDeviceSuggestions([]);
    setHighlightedSuggestionIndex(-1);
  }

  function removeSelectedDevice(id) {
    setSelectedDevices(prev => prev.filter(d => d.id !== id));
  }

  async function addDevicesToGroup() {
    if (selectedDevices.length === 0) return;

    try {
      setAddingDevices(true);

      const res = await fetch(
        `${API_BASE}/entityGroup/${groupId}/addEntities`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(
            selectedDevices.map(d => d.id)
          )
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Endgeräte konnten nicht hinzugefügt werden (HTTP ${res.status})`);
      }

      showStatus(`${selectedDevices.length} Endgerät(e) wurden erfolgreich hinzugefügt.`);

      setShowAddDevicesModal(false);
      setSelectedDevices([]);
      setDeviceSearch("");

      // Neu laden
      setPage(0);
      await loadAll();
    } catch (err) {
      showStatus(`Endgeräte konnten nicht hinzugefügt werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setAddingDevices(false);
    }
  }


  async function ackAlarm(alarm) {
    await updateAlarmStatus(alarm, "ack", "ACTIVE_ACK");
  }

  async function clearAlarm(alarm) {
    await updateAlarmStatus(alarm, "clear", "CLEARED_ACK");
  }

  async function updateAlarmStatus(alarm, action, newStatus) {
    await updateAlarmStatusAPI(alarm.id.id, action, token);

    setAlarms(prev =>
      prev.map(a =>
        a.id.id === alarm.id.id ? { ...a, status: newStatus } : a
      )
    );

    setOpenAlarmStatusMenu(null);

    const labels = { ACTIVE_ACK: 'Alarm bestätigt', CLEARED_ACK: 'Alarm behoben' };
    showStatus(labels[newStatus] || 'Status geändert');
  }

  async function deleteGroup() {
    try {
      setDeletingGroup(true);

      const res = await fetch(
        `${API_BASE}/entityGroup/${groupId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Gruppe konnte nicht gelöscht werden (HTTP ${res.status})`);
      }

      // Nach erfolgreichem Löschen zurück zur vorherigen Seite oder Übersicht
      navigate("/device-groups", {
        replace: true,
        state: {
          statusMessage: `Gruppe „${group.name}“ wurde erfolgreich gelöscht.`
        }
      });

    } catch (err) {
      console.error(err);
      showStatus(`Die Gruppe konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeletingGroup(false);
      setShowGroupDeleteConfirm(false);
    }
  }

  async function removeDeviceFromGroup() {
    if (!entityToRemove) return;

    try {
      setRemoving(true);

      const res = await fetch(
        `${API_BASE}/entityGroup/${groupId}/deleteEntities`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify([
            
              entityToRemove.id
            
          ])
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Endgerät konnte nicht aus der Gruppe entfernt werden (HTTP ${res.status})`);
      }

      // UI sofort aktualisieren
      setDevices(prev => prev.filter(d => d.id.id !== entityToRemove.id));
      setTotalDevices(c => Math.max(0, c - 1));

      showStatus(`Endgerät „${entityToRemove.name}” wurde erfolgreich aus der Gruppe entfernt.`);

    } catch (err) {
      console.error(err);
      showStatus(`Endgerät konnte nicht aus der Gruppe entfernt werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setRemoving(false);
      setShowRemoveConfirm(false);
      setEntityToRemove(null);
    }
  }

  if (loading) return <p>Lade Details...</p>;
  if (error) return <div role="alert">{error}</div>;
  if (!group) return null;

 return (
    <div className="group-detail-page">

      {/* Header */}
      <div className="device-detail-header">
        <div className="header-left">
          <button
            className={"back-button"}
            onClick={()=> navigate(-1)}
            aria-label="Zurück zur vorherigen Seite"
            title="Zurück zur vorherigen Seite"
          >
            <span aria-hidden="true"><Zurück /></span>
          </button>
          <h1 id="device-title" tabIndex={-1} ref={titleRef} className="device-title">
            {group.name}
          </h1>
        </div>

        <div className="device-actions">
          
          <button
            className="action-buttons"
            aria-label="Daten exportieren"
            title="Daten exportieren"
            disabled
          >
            <div className="action-button-icon"><Export /></div>
            <div>Daten exportieren</div>
          </button>

          <button
            className="action-buttons"
            aria-label="Daten analysieren"
            title="Daten analysieren"
            disabled
          >
            <div className="action-button-icon"><AiAnalyse2 /></div>
            <div>Daten analysieren</div>
          </button>


          <button
            className="action-buttons"
            aria-label="Gruppe löschen"
            title="Gruppe löschen"
            onClick={() => setShowGroupDeleteConfirm(true)}
          >
            <div className="action-button-icon"><Löschen /></div>
            <div>Gruppe löschen</div>
          </button>

        </div>
      </div>

      <StatusBar message={statusMessage} statusType={statusType} />

      {/* Content Grid */}
      <main id="main-content" role="main" aria-labelledby="device-title" className="device-detail-grid">

        <div className="card-wrapper">
          <h2 className="card-title">Endgeräte ({totalDevices})</h2>
          {/* Alle Geräte */}
          <section className="card">
            <div className="devices-list-area">
              <div className="devices-list-scroll">
                {devices.map(device => (
                  <div key={device.id.id} className="list-item alarm-item clickable-row"
                    onClick={(e) => {
                      if (e.target.closest('button, .sort-wrapper')) return;
                      navigate(`/devices/${device.id.id}`, { state: { name: device.name } });
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .sort-wrapper')) {
                        e.preventDefault();
                        navigate(`/devices/${device.id.id}`, { state: { name: device.name } });
                      }
                    }}
                    tabIndex={0}
                    aria-label={`Endgerät ${device.name} öffnen`}
                  >

                    <div className="alarm-main">
                      <span className="alarm-name">{device.name}</span>
                    </div>

                    <div className="actions">

                      <IconButton variant="open" label={`Endgerät ${device.name} öffnen`}
                        onClick={() => navigate(`/devices/${device.id.id}`, { state: { name: device.name } })} />

                      <IconButton variant="delete" label={`Endgerät ${device.name} aus der Gruppe entfernen`}
                        onClick={() => {
                          setEntityToRemove({ id: device.id.id, name: device.name });
                          setShowRemoveConfirm(true);
                        }} />

                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalDevices > 0 && (
                <Pagination page={page} totalPages={totalPages} totalItems={totalDevices} pageSize={PAGE_SIZE} onPageChange={setPage} label="Geräte" />
              )}
            </div>

            <button className="edit add-alarm"
              aria-label="Endgerät zur Gruppe hinzufügen"
              title="Endgerät zur Gruppe hinzufügen"
              onClick={() => setShowAddDevicesModal(true)}
            >
              <span aria-hidden="true"><Hinzufügen /></span> Endgerät zur Gruppe hinzufügen
            </button>

          </section>
        </div>
        
        <div className="card-wrapper">
          <h2 className="card-title">Letzte Telemetriedaten</h2>
          {/* Telemetrie */}
          <section className="card telemetry-card" aria-live="polite" aria-busy={loading}>
            <div className="telemetry-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Gesendet</th>
                    <th scope="col">Schlüssel</th>
                    <th scope="col">Wert</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(telemetry).map(([key, values]) =>
                    values.map((v, idx) => (
                      <tr key={`${key}-${idx}`}>
                        <td>{formatTelemetryTimestamp(v.ts)}</td>
                        <td>{key}</td>
                        <td>{v.value}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="card-wrapper">
          <h2 className="card-title">Dashboards ({dashboards.length})</h2>
          {/* Dashboards */}
          <section className="card" aria-live="polite" aria-busy={loading}>
            {dashboards.map(d => (
              <div key={d.id.id} className="list-item">
                {d.title}
                <button aria-label={`Dashboard ${d.title} öffnen`}>
                  <span aria-hidden="true">↗</span>
                </button>
              </div>
            ))}
            <button className="edit add-alarm"
              aria-label="Neues Dashboard erstellen"
              title="Neues Dashboard erstellen"
              disabled
            >
              ＋ Neues Dashboard erstellen
            </button>
          </section>
        </div>      
        
        
        <div className="card-wrapper">
          <h2 className="card-title">Alarme ({alarms.length})</h2>
          {/* Alarme */}
          <section className="card">
            {alarms.map(a => (
              <div key={a.id.id} className="list-item alarm-item">

                <div className="alarm-main">
                  <span className="alarm-name">{a.name}</span>

                  <span className={`severity-badge ${a.severity.toLowerCase()}`}>
                    {severityLabels[a.severity]}
                  </span>
                </div>

                <div className="actions">

                  <AlarmStatusDropdown
                    alarmId={a.id.id}
                    status={a.status}
                    isOpen={openAlarmStatusMenu === a.id.id}
                    onToggle={() => setOpenAlarmStatusMenu(openAlarmStatusMenu === a.id.id ? null : a.id.id)}
                    onAck={() => ackAlarm(a)}
                    onClear={() => clearAlarm(a)}
                    onClose={() => setOpenAlarmStatusMenu(null)}
                  />

                  <button title="Details öffnen" aria-label="Details öffnen"><span aria-hidden="true">↗</span></button>
                  <button title="Alarm löschen" aria-label="Alarm löschen"><span aria-hidden="true">🗑</span></button>
                </div>

              </div>
            ))}

            <button className="edit add-alarm"
              aria-label="Neuen Alarm erstellen"
              title="Neuen Alarm erstellen"
              disabled
            >
              ＋ Neuen Alarm erstellen
            </button>

          </section>
        </div>

      </main>

      {showGroupDeleteConfirm && (
        <DeleteConfirmModal
          titleId="group-delete-title"
          title={<>Gruppe <i>{group?.name}</i> löschen?</>}
          body={<p>Diese Aktion ist <strong>unwiderruflich</strong>. Alle zugehörigen Daten werden gelöscht.</p>}
          onConfirm={deleteGroup}
          onCancel={() => setShowGroupDeleteConfirm(false)}
          loading={deletingGroup}
        />
      )}

      {showRemoveConfirm && entityToRemove && (
        <DeleteConfirmModal
          titleId="remove-entity-title"
          title={<>Gerät <i>{entityToRemove.name}</i> entfernen?</>}
          body={<p>Das Gerät wird aus dieser Gruppe entfernt, aber <strong>nicht</strong> gelöscht.</p>}
          onConfirm={removeDeviceFromGroup}
          onCancel={() => { setShowRemoveConfirm(false); setEntityToRemove(null); }}
          loading={removing}
        />
      )}

      {showAddDevicesModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="add-devices-title">
          <div className="modal large" ref={addDevicesModalRef}>

            <h2 id="add-devices-title">Endgeräte zur Gruppe<br /><i>{group.name}</i> hinzufügen
              <button
                type="button"
                className={`tooltip-icon-button ${showAddDevicesTooltip ? "active" : ""}`}
                aria-label="Informationen zum Hinzufügen von Endgeräten"
                aria-expanded={showAddDevicesTooltip}
                aria-controls="add-devices-tooltip"
                onClick={() => setShowAddDevicesTooltip(prev => !prev)}
                onKeyDown={e => {
                  if (e.key === "Escape") {
                    setShowAddDevicesTooltip(false);
                  }
                }}
              >
                <span aria-hidden="true"><Hinweis /></span>
              </button>
            </h2>

            {showAddDevicesTooltip && (
              <div
                id="add-devices-tooltip"
                role="tooltip"
                ref={tooltipRef}
                className="custom-tooltip"
              >
                Suchen Sie nach eingebundenen Endgeräten über die Suchfunktion. Geben Sie dazu den Namen eines Endgeräts ein. 
                Wählen Sie mindestens ein Endgerät aus.
                Bereits hinzugefügte Geräte werden nicht angezeigt.
              </div>
            )}


            {/* Suche */}
            <ModalDeviceSearch
              searchValue={deviceSearch}
              onSearchChange={setDeviceSearch}
              suggestions={deviceSuggestions}
              showSuggestions={showDeviceSuggestions}
              onFocusShow={() => setShowDeviceSuggestions(true)}
              onBlurHide={() => setTimeout(() => setShowDeviceSuggestions(false), 150)}
              onHideSuggestions={() => setShowDeviceSuggestions(false)}
              highlightedIndex={highlightedSuggestionIndex}
              onHighlightChange={setHighlightedSuggestionIndex}
              onSelect={addDevice}
              onClearSearch={() => { setDeviceSearch(''); setHighlightedSuggestionIndex(-1); }}
              onEscapeClose={() => setShowAddDevicesModal(false)}
              suggestionsRef={deviceSuggestionsRef}
              selectedDevices={selectedDevices}
              onRemoveDevice={removeSelectedDevice}
            />

            {/* Actions */}
            <div className="modal-actions">
              <button
                className="modal-primary-positive"
                onClick={addDevicesToGroup}
                disabled={addingDevices || selectedDevices.length === 0}
              >
                {addingDevices
                  ? "Hinzufügen…"
                  : <><Hinzufügen /> Endgeräte hinzufügen</>}
              </button>

              <button
                className="modal-secondary"
                onClick={() => {
                  setShowAddDevicesModal(false);
                  setSelectedDevices([]);
                  setDeviceSearch("");
                  setHighlightedSuggestionIndex(-1);
                }}
                disabled={addingDevices}
              >
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );

}

export default GroupDetail;